/* @flow */

import * as React from 'react';

import {Button} from '../button';
import {colors} from '../colors';
import type {ColorName} from '../colors/colors';
import {Stack} from '../layout';
import {Link} from '../link';
import {ProgressBar} from '../progress-bar';
import {Body} from '../typography/body';

import {TrialGatheringPrompt} from './trial-gathering-prompt';

import './trial-gathering-layout.module.css';

type Props = {|
    animation: React.Node,
    animationBackgroundColor?: ColorName,
    prompt: string,
    instructions?: string,
    children: React.Node, // Form fields
    currentStep: number,
    totalSteps: number,
    onSkip?: () => void,
    onNext?: () => void,
    onBack?: () => void,
|};

export function TrialGatheringLayout(props: Props) {
    const animationStyleVariables = props.animationBackgroundColor
        ? {
              '--animation-bg-color': colors[props.animationBackgroundColor],
          }
        : undefined;

    return (
        <Stack isHorizontal={true} isFullWidth={true}>
            <div styleName='left-column'>
                <Stack spacing={16} isFullHeight={true}>
                    <Link href='https://www.nutshell.com/home' newTab={true}>
                        <img
                            styleName='nutshell-logo'
                            src='/include/images/nutshell-logo-full-color.svg'
                            alt='nutshell logo'
                        />
                    </Link>
                    <div styleName='content-container'>
                        <Stack spacing={16}>
                            <div styleName='progress'>
                                <Body size='small' color='light'>
                                    Step {props.currentStep} of {props.totalSteps}
                                </Body>
                                <ProgressBar
                                    percentComplete={props.currentStep / props.totalSteps}
                                    showPercentage={false}
                                    roundedCorners={true}
                                    backgroundColor={colors.blueLt}
                                    color={colors.blue}
                                    height={8}
                                />
                            </div>
                            <div styleName='form-container'>
                                <div>
                                    <TrialGatheringPrompt>{props.prompt}</TrialGatheringPrompt>
                                    <Body size='large' color='light'>
                                        {props.instructions}
                                    </Body>
                                </div>
                                <div>{props.children}</div>
                                <Stack
                                    isHorizontal={true}
                                    spacing={16}
                                    justifyContent={props.onSkip ? 'space-between' : 'end'}
                                >
                                    {props.onSkip && (
                                        <Button
                                            variant='text-secondary'
                                            size='big'
                                            onClick={props.onSkip}
                                        >
                                            Skip
                                        </Button>
                                    )}
                                    <Stack isHorizontal={true} spacing={16}>
                                        {props.currentStep > 1 && (
                                            <Button
                                                variant='secondary'
                                                size='big'
                                                onClick={props.onBack}
                                            >
                                                Back
                                            </Button>
                                        )}
                                        <Button
                                            variant={
                                                props.currentStep === props.totalSteps
                                                    ? 'save'
                                                    : 'primary'
                                            }
                                            size='big'
                                            onClick={props.onNext}
                                        >
                                            {props.currentStep === props.totalSteps
                                                ? 'Done!'
                                                : 'Continue'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </div>
                        </Stack>
                    </div>
                </Stack>
            </div>
            <div styleName='animation' style={animationStyleVariables}>
                {props.animation}
            </div>
        </Stack>
    );
}
